import Styles from './setup.module.scss';
import React, { useState, useCallback, useMemo } from 'react';
import {
    Typography,
    TextField,
} from '@mui/material';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import axios from 'axios';

export const ProfileInfoForm = ({ onSuccess, initialData, context, authContext }) => {
    const [formData, setFormData] = useState({
        firstName: initialData.firstName || '',
        lastName: initialData.lastName || '',
        zipCode: initialData.zipCode || '',
        address: initialData.address || '',
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const validateField = useCallback((name, value) => {
        switch (name) {
            case 'firstName':
            case 'lastName':
                if (!value.trim()) return 'This field is required';
                if (value.length > 50) return 'Must be 50 characters or less';
                if (!/^[a-zA-Z][a-zA-Z]*$/.test(value)) return 'Must contain only letters';
                break;
            case 'zipCode':
                return /^\d{6}$/.test(value) ? '' : 'Enter a valid 6-digit ZIP code';
            default:
                return '';
        }
    }, []);

    const handleChange = useCallback((field) => (e) => {
        const { value } = e.target;
        setFormData(prev => ({ ...prev, [field]: value }));
        setErrors(prev => ({ ...prev, [field]: validateField(field, value) }));
    }, [validateField]);

    const isFormValid = useMemo(() =>
        formData.firstName.trim() &&
        formData.lastName.trim() &&
        /^\d{6}$/.test(formData.zipCode) &&
        !Object.values(errors).some(error => error),
        [formData, errors]
    );

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!isFormValid) {
            setErrors({
                firstName: validateField('firstName', formData.firstName),
                lastName: validateField('lastName', formData.lastName),
                zipCode: validateField('zipCode', formData.zipCode),
            });
            return;
        }

        setLoading(true);
        try {
            const payload = {
                first_name: formData.firstName.trim(),
                last_name: formData.lastName.trim(),
                zip_code: formData.zipCode,
                ...(formData.address.trim() && { address: formData.address.trim() })
            };

            const response = await axios.patch('/api/user', payload);

            if (response.status === 200) {
                authContext.update({ last_name: response?.data?.data?.last_name, onboarded: 1 })
                context.notification.show(response?.data?.message || "Profile updated successfully", 'success', true);
                onSuccess(formData);
                window.location.reload();
            } else {
                context.notification.show('Failed to update profile. Please try again', 'error')
            }
        } catch (error) {
            if (error.response?.data?.message) {
                context.notification.show(error.response.data?.message, 'error');
            } else {
                context.notification.show(error.response.data, 'error');
            }
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, isFormValid, onSuccess, context, validateField]);

    const textFieldProps = useMemo(() => ({
        fullWidth: true,
        size: "small",
        margin: "dense",
        disabled: loading,
    }), [loading]);

    return (
        <>
            <Typography sx={{ fontWeight: 600, marginBottom: '1em', color: '#1450a0' }} variant="h5">Update Your Profile Information</Typography>
            <div className={Styles.formContainer}>
                <TextField
                    {...textFieldProps}
                    label="First Name"
                    value={formData.firstName}
                    onChange={handleChange('firstName')}
                    error={!!errors.firstName}
                    helperText={errors.firstName || 'To update your first name, please contact our support team.'}
                    disabled={true}
                    fullWidth
                    required
                    slotProps={{
                        input: {
                            style: {
                                height: "50px",
                            }
                        }
                    }}
                />
                <TextField
                    {...textFieldProps}
                    label="Last Name"
                    value={formData.lastName}
                    onChange={handleChange('lastName')}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    fullWidth
                    required
                    slotProps={{
                        input: {
                            style: {
                                height: "50px",
                            }
                        }
                    }}
                />
                <TextField
                    {...textFieldProps}
                    label="Address"
                    value={formData.address}
                    onChange={handleChange('address')}
                    error={!!errors.address}
                    helperText={errors.address}
                    fullWidth
                    slotProps={{
                        input: {
                            style: {
                                height: "50px",
                            }
                        }
                    }}
                />
                <TextField
                    {...textFieldProps}
                    label="Zip Code"
                    value={formData.zipCode}
                    type="text"
                    onChange={handleChange('zipCode')}
                    error={!!errors.zipCode || !!errors.general}
                    helperText={errors.zipCode || errors.general}
                    fullWidth
                    required
                    slotProps={{
                        input: {
                            style: {
                                height: "50px",
                            }
                        }
                    }}
                />
            </div>
            <div id='updateProfileButton' className={Styles.buttonContainer}>
                <AivagamButton
                    buttonText={loading ? "Updating..." : "Update Profile"}
                    disabled={loading || !isFormValid}
                    onClick={handleSubmit}
                    classStyles={{
                        fontSize: '1em',
                        height: '3em',
                        padding: "0.5em 2em",
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
                    }}
                />
            </div>
        </>
    );
};